import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { api, unwrap } from '../api/client.js';
import { formatNumber, intlLocale } from '../i18n.js';
import { useLocale } from './site.js';

export type Range = readonly number[];
export const lo = (r: Range) => r[0] ?? 0;
export const hi = (r: Range) => r[1] ?? 0;

/** Classic development and AI-assisted development: one colour each, everywhere. */
export const CLASSIC = '#94a3b8';
export const AI = '#7c3aed';

export function useModernization(siteId: string) {
  const locale = useLocale();
  return useQuery({
    queryKey: ['site', siteId, 'modernization', locale],
    queryFn: () =>
      unwrap(
        api.GET('/sites/{siteId}/modernization', {
          params: { path: { siteId }, query: { locale } },
        }),
      ),
  });
}

export type Modernization = NonNullable<ReturnType<typeof useModernization>['data']>;
export type ModernPackage = Modernization['packages'][number];

export function useFormat() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const euro = (v: number) =>
    new Intl.NumberFormat(intlLocale(lang), {
      style: 'currency',
      currency: 'EUR',
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: Number.isInteger(v) ? 0 : 2,
      maximumFractionDigits: Number.isInteger(v) ? 0 : 2,
    }).format(v);
  const num = (v: number, digits = 1) => formatNumber(v, lang, digits);
  return {
    euro,
    euroRange: (r: Range) => `${euro(lo(r))} – ${euro(hi(r))}`,
    hours: (r: Range) => t('modern.hoursRange', { from: num(lo(r)), to: num(hi(r)) }),
    seconds: (ms: number) => t('modern.seconds', { value: num(ms / 1000) }),
    megabytes: (bytes: number) => t('modern.megabytes', { value: num(bytes / 1_000_000) }),
    percent: (share: number) => `${num(share * 100, 0)} %`,
    months: (r: Range) =>
      hi(r) < 1
        ? t('modern.paybackUnderMonth')
        : t('modern.paybackMonths', { from: num(Math.max(lo(r), 1), 0), to: num(hi(r), 0) }),
    num,
  };
}

/** Two bars on one scale: classic development and with AI. */
export function EffortBars({ hours, max }: { hours: ModernPackage['hours']; max: number }) {
  const { t } = useTranslation();
  const f = useFormat();
  const width = (h: number) => `${Math.max(2, (h / max) * 100)}%`;
  return (
    <div className="flex flex-col gap-1">
      {(
        [
          ['classic', hours.classic, CLASSIC],
          ['ai', hours.ai, AI],
        ] as const
      ).map(([key, range, color]) => (
        <div key={key} className="flex items-center gap-2">
          <span className="h-2.5 flex-1 rounded bg-slate-100">
            <span
              className="block h-2.5 rounded"
              style={{ width: width(hi(range)), background: color }}
            />
          </span>
          <span className="w-36 shrink-0 text-sm text-slate-700 tabular-nums">
            {f.hours(range)} {key === 'ai' ? t('modern.withAi') : ''}
          </span>
        </div>
      ))}
    </div>
  );
}

export function Legend() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap gap-4 text-sm text-slate-700">
      <span className="flex items-center gap-2">
        <span aria-hidden="true" className="h-3 w-3 rounded-sm" style={{ background: CLASSIC }} />
        {t('modern.classic')}
      </span>
      <span className="flex items-center gap-2">
        <span aria-hidden="true" className="h-3 w-3 rounded-sm" style={{ background: AI }} />
        {t('modern.ai')}
      </span>
    </div>
  );
}

/**
 * Cost (with AI, upper bound, log scale) against benefit: top left are the quick wins. Drawn from
 * the packages the site actually gets.
 */
export function EffortBenefitMatrix({ packages }: { packages: readonly ModernPackage[] }) {
  const { t } = useTranslation();
  const f = useFormat();
  const W = 640;
  const H = 380;
  const left = 70;
  const right = 610;
  const top = 20;
  const bottom = 340;
  const logMin = Math.log10(100);
  const logMax = Math.log10(20_000);
  const x = (euro: number) =>
    left +
    ((Math.log10(Math.min(Math.max(euro, 100), 20_000)) - logMin) / (logMax - logMin)) *
      (right - left);
  const y = (benefit: number) => bottom - (benefit / 100) * (bottom - top);
  const midX = (left + right) / 2;
  const midY = (top + bottom) / 2;
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="h-auto w-full"
      role="img"
      aria-label={t('modern.matrix.description', {
        list: packages
          .map((p) => `${t(`modern.packages.${p.key}.title`)}: ${f.euroRange(p.cost.ai)}`)
          .join('; '),
      })}
    >
      <rect x={left} y={top} width={midX - left} height={midY - top} className="fill-green-50" />
      <rect x={midX} y={top} width={right - midX} height={midY - top} className="fill-blue-50" />
      <rect
        x={left}
        y={midY}
        width={midX - left}
        height={bottom - midY}
        className="fill-slate-50"
      />
      <rect x={midX} y={midY} width={right - midX} height={bottom - midY} className="fill-red-50" />
      <text x={left + 10} y={top + 20} className="fill-green-800 text-[13px] font-bold">
        {t('modern.matrix.quickWins')}
      </text>
      <text
        x={right - 10}
        y={top + 20}
        textAnchor="end"
        className="fill-blue-800 text-[13px] font-bold"
      >
        {t('modern.matrix.bigProjects')}
      </text>
      <text x={left + 10} y={bottom - 10} className="fill-slate-600 text-[13px] font-bold">
        {t('modern.matrix.later')}
      </text>
      <text
        x={right - 10}
        y={bottom - 10}
        textAnchor="end"
        className="fill-red-800 text-[13px] font-bold"
      >
        {t('modern.matrix.skip')}
      </text>
      <line x1={left} y1={bottom} x2={right} y2={bottom} className="stroke-slate-400" />
      <line x1={left} y1={top} x2={left} y2={bottom} className="stroke-slate-400" />
      <text
        x={(left + right) / 2}
        y={H - 12}
        textAnchor="middle"
        className="fill-slate-600 text-[13px]"
      >
        {t('modern.matrix.xAxis')}
      </text>
      <text
        x={24}
        y={(top + bottom) / 2}
        textAnchor="middle"
        transform={`rotate(-90 24 ${(top + bottom) / 2})`}
        className="fill-slate-600 text-[13px]"
      >
        {t('modern.matrix.yAxis')}
      </text>
      {packages.map((p) => {
        const cx = x(hi(p.cost.ai));
        const cy = y(p.benefit);
        const anchorEnd = cx > right - 150;
        return (
          <g key={p.key}>
            <circle
              cx={cx}
              cy={cy}
              r={p.recommended ? 16 : 13}
              className={p.recommended ? 'fill-green-700' : 'fill-blue-700'}
            />
            <text
              x={anchorEnd ? cx - 22 : cx + 22}
              y={cy - 2}
              textAnchor={anchorEnd ? 'end' : 'start'}
              className="fill-slate-900 text-[13px] font-semibold"
            >
              {t(`modern.packages.${p.key}.title`)}
            </text>
            <text
              x={anchorEnd ? cx - 22 : cx + 22}
              y={cy + 14}
              textAnchor={anchorEnd ? 'end' : 'start'}
              className="fill-slate-600 text-[12px]"
            >
              {f.euroRange(p.cost.ai)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/** Cumulative result over 12 months: the AI-assisted cost first, then the monthly gain. */
export function PaybackChart({ cost, gain }: { cost: number; gain: Range }) {
  const { t } = useTranslation();
  const f = useFormat();
  const months = 12;
  const endHigh = -cost + hi(gain) * months;
  const endLow = -cost + lo(gain) * months;
  const maxV = Math.max(endHigh, 1000);
  const minV = -cost * 1.2;
  const left = 80;
  const right = 620;
  const top = 24;
  const bottom = 250;
  const x = (m: number) => left + (m / months) * (right - left);
  const y = (v: number) => top + ((maxV - v) / (maxV - minV)) * (bottom - top);
  const step = niceStep(maxV);
  const ticks = [0, step, step * 2, step * 3].filter((v) => v <= maxV);
  const payback = cost / Math.max(lo(gain), 1);
  return (
    <svg
      viewBox="0 0 660 300"
      className="h-auto w-full"
      role="img"
      aria-label={t('modern.payback.description', {
        cost: f.euro(cost),
        low: f.euro(endLow),
        high: f.euro(endHigh),
      })}
    >
      {ticks.map((v) => (
        <g key={v}>
          <line
            x1={left}
            x2={right}
            y1={y(v)}
            y2={y(v)}
            className={v === 0 ? 'stroke-slate-500' : 'stroke-slate-200'}
            strokeWidth={v === 0 ? 1.5 : 1}
          />
          <text x={left - 8} y={y(v) + 4} textAnchor="end" className="fill-slate-600 text-[12px]">
            {f.euro(v)}
          </text>
        </g>
      ))}
      <line
        x1={x(0)}
        y1={y(-cost)}
        x2={x(months)}
        y2={y(endHigh)}
        className="stroke-green-700"
        strokeWidth={3}
      />
      <line
        x1={x(0)}
        y1={y(-cost)}
        x2={x(months)}
        y2={y(endLow)}
        className="stroke-green-700"
        strokeWidth={3}
        strokeDasharray="7 5"
      />
      {payback <= months ? (
        <g>
          <circle cx={x(payback)} cy={y(0)} r={6} className="fill-slate-900" />
          <text
            x={x(payback) + 10}
            y={y(0) - 10}
            className="fill-slate-900 text-[12px] font-semibold"
          >
            {t('modern.payback.point')}
          </text>
        </g>
      ) : null}
      <text
        x={right}
        y={y(endHigh) - 10}
        textAnchor="end"
        className="fill-green-800 text-[12px] font-semibold"
      >
        +{f.euro(endHigh)}
      </text>
      <text
        x={right}
        y={y(endLow) + 20}
        textAnchor="end"
        className="fill-green-800 text-[12px] font-semibold"
      >
        +{f.euro(endLow)}
      </text>
      {[0, 3, 6, 9, 12].map((m) => (
        <text
          key={m}
          x={x(m)}
          y={bottom + 24}
          textAnchor="middle"
          className="fill-slate-600 text-[12px]"
        >
          {m === 12 ? t('modern.payback.months', { count: 12 }) : m}
        </text>
      ))}
    </svg>
  );
}

function niceStep(max: number) {
  const raw = max / 3;
  const pow = 10 ** Math.floor(Math.log10(raw));
  return Math.ceil(raw / pow) * pow;
}

/** Before → after as two bars on the same scale (red now, green after). */
export function BeforeAfterRow({
  label,
  now,
  after,
  format,
  higherIsBetter = false,
}: {
  label: ReactNode;
  now: number;
  after: number;
  format: (v: number) => string;
  higherIsBetter?: boolean;
}) {
  const max = higherIsBetter ? 100 : Math.max(now, after);
  const width = (v: number) => `${Math.max(2, (v / max) * 100)}%`;
  return (
    <div className="grid gap-2 sm:grid-cols-[14rem_minmax(0,1fr)] sm:items-center">
      <span className="font-semibold">{label}</span>
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="h-3.5 flex-1 rounded bg-slate-100">
            <span className="block h-3.5 rounded bg-red-600" style={{ width: width(now) }} />
          </span>
          <span className="w-24 shrink-0 tabular-nums">{format(now)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="h-3.5 flex-1 rounded bg-slate-100">
            <span className="block h-3.5 rounded bg-green-700" style={{ width: width(after) }} />
          </span>
          <span className="w-24 shrink-0 tabular-nums">≈ {format(after)}</span>
        </div>
      </div>
    </div>
  );
}
